import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Calculator, Sheet, Bot, BarChart3, Package, UserCheck, Mail,
  TrendingDown, LogIn, Truck, MessageSquare, DollarSign, Shield, LogOut, Lock,
  Moon, Sun, Menu, GitBranch, Megaphone, HandshakeIcon, Inbox, Target,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import UnifiedAssistantModal from './UnifiedAssistantModal';
import FloatingHelpBot from './FloatingHelpBot';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

/**
 * App shell.
 *  - Left rail: grouped, icon-leading nav with gold active indicator.
 *  - Logo box at top of rail — click to toggle collapse/expand.
 *  - Footer: user info + theme + change-password + sign-out.
 *  - Mobile: floating hamburger opens the rail as a drawer.
 *  - No top header; pages own their header via PageHeader.
 */
const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('mm-sidebar-collapsed') === '1'; } catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed(v => {
      const next = !v;
      try { localStorage.setItem('mm-sidebar-collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const { employee, logout, hasAccess } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/employee-login');
  };

  // Grouped nav. Items without `page` are always shown (handled by hasAccess default).
  const navGroups = [
    {
      label: 'Overview',
      items: [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard', page: 'dashboard', exact: true },
        { path: '/analytics', icon: BarChart3, label: 'Analytics', page: 'analytics' },
        { path: '/marketing', icon: Target, label: 'Marketing', page: 'analytics' },
      ],
    },
    {
      label: 'Sales',
      items: [
        { path: '/sales-pipeline', icon: GitBranch, label: 'Pipeline', page: 'sales-pipeline' },
        { path: '/retail-worksheet', icon: Sheet, label: 'Retail Worksheet', page: 'retail-worksheet' },
        { path: '/brokered-deals', icon: HandshakeIcon, label: 'Brokered Deals', page: 'brokered-deals' },
        { path: '/broker-portal', icon: Inbox, label: 'Broker Portal', page: 'broker-portal' },
        { path: '/calculator', icon: Calculator, label: 'Calculator', page: 'calculator' },
      ],
    },
    {
      label: 'Operations',
      items: [
        { path: '/inventory', icon: Package, label: 'Inventory', page: 'inventory' },
        { path: '/delivery', icon: Truck, label: 'Deliveries', page: 'delivery' },
        { path: '/trade-in-estimator', icon: TrendingDown, label: 'Appraisals', page: 'trade-in-estimator' },
      ],
    },
    {
      label: 'Customers',
      items: [
        { path: '/customers', icon: Users, label: 'Customers', page: 'customers' },
        { path: '/customer-messages', icon: MessageSquare, label: 'Messages', page: 'customer-messages' },
        { path: '/customer-portal-management', icon: LogIn, label: 'Portal', page: 'customer-portal-management' },
        { path: '/email-campaigns', icon: Mail, label: 'Email Campaigns', page: 'email-campaigns' },
      ],
    },
    {
      label: 'Team & Finance',
      items: [
        { path: '/sales-staff', icon: UserCheck, label: 'Sales Staff', page: 'sales-staff' },
        { path: '/accounting', icon: DollarSign, label: 'Accounting', page: 'accounting' },
        { path: '/promotions', icon: Megaphone, label: 'Promotions', page: 'promotions' },
      ],
    },
  ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Keyboard users can jump straight to content, past the nav rail */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-3 focus:py-2 focus:bg-card focus:text-foreground focus:rounded-md focus:ring-2 focus:ring-gold-500"
      >
        Skip to main content
      </a>
      {/* Mobile hamburger — floating top-left when drawer closed */}
      {!mobileMenuOpen && (
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="lg:hidden fixed top-3 left-3 z-40 p-2 rounded-md bg-card border border-border shadow-sm hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
          aria-label="Open navigation"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-card border-r border-border flex flex-col overflow-hidden flex-shrink-0
          lg:relative lg:translate-x-0
          fixed inset-y-0 left-0 z-50 transition-all duration-200
          ${sidebarCollapsed ? 'w-14' : 'w-56'}
          ${mobileMenuOpen ? 'translate-x-0 w-56' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo box — click toggles collapse on desktop, closes drawer on mobile */}
        <div className="border-b border-border px-2 py-1">
          <button
            onClick={() => {
              if (window.matchMedia('(min-width: 1024px)').matches) toggleSidebar();
              else setMobileMenuOpen(false);
            }}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="w-full flex items-center justify-center rounded-md hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
          >
            <img
              src="/logo-modernmotors.png"
              alt="Modern-Motors"
              className={`object-contain shrink-0 ${sidebarCollapsed ? 'h-7 w-7' : 'h-[52px] w-auto max-w-full'}`}
            />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-4">
            {navGroups.map(group => {
              const visibleItems = group.items.filter(item => !item.page || hasAccess(item.page));
              if (visibleItems.length === 0) return null;
              return (
                <div key={group.label}>
                  {!sidebarCollapsed && (
                    <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                      {group.label}
                    </div>
                  )}
                  <ul className="space-y-0.5">
                    {visibleItems.map(item => {
                      const Icon = item.icon;
                      const active = isActive(item);
                      return (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            title={sidebarCollapsed ? item.label : undefined}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`group relative flex items-center gap-2.5 ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-1.5 rounded-md text-[13px] transition-colors ${
                              active
                                ? 'bg-ink-950 text-white dark:bg-ink-800'
                                : 'text-ink-700 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800'
                            }`}
                          >
                            {active && (
                              <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-gold-500" />
                            )}
                            <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-gold-400' : 'text-ink-500 group-hover:text-ink-700 dark:group-hover:text-ink-200'}`} />
                            {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}

            {/* Assistants — hidden for roles that never use them */}
            {employee?.role !== 'Accountant' && employee?.role !== 'Used Car Manager' && (
              <div>
                {!sidebarCollapsed && (
                  <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                    Assistants
                  </div>
                )}
                <ul className="space-y-0.5">
                  <li>
                    <button
                      onClick={() => setShowAssistantModal(true)}
                      title={sidebarCollapsed ? 'AI Assistant' : undefined}
                      className={`w-full flex items-center gap-2.5 ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-1.5 rounded-md text-[13px] text-ink-700 dark:text-ink-300 hover:bg-gold-50 dark:hover:bg-gold-950/40 hover:text-gold-700 dark:hover:text-gold-300 transition-colors`}
                    >
                      <Bot className="w-4 h-4 shrink-0" />
                      {!sidebarCollapsed && <span>AI Assistant</span>}
                    </button>
                  </li>
                </ul>
              </div>
            )}

            {(employee?.role === 'Admin' || employee?.role === 'Sales Manager') && (
              <div>
                {!sidebarCollapsed && (
                  <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-400">
                    {employee?.role === 'Admin' ? 'Admin' : 'Team'}
                  </div>
                )}
                <ul className="space-y-0.5">
                  <li>
                    <Link
                      to="/admin-panel"
                      title={sidebarCollapsed ? 'Admin Panel' : undefined}
                      className={`flex items-center gap-2.5 ${sidebarCollapsed ? 'justify-center px-2' : 'px-3'} py-1.5 rounded-md text-[13px] transition-colors ${
                        location.pathname === '/admin-panel'
                          ? 'bg-ink-950 text-white dark:bg-ink-800'
                          : 'text-ink-700 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800'
                      }`}
                    >
                      <Shield className={`w-4 h-4 ${location.pathname === '/admin-panel' ? 'text-gold-400' : 'text-ink-500'}`} />
                      {!sidebarCollapsed && <span>{employee?.role === 'Admin' ? 'Admin Panel' : 'Team Passwords'}</span>}
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </nav>

        <div className={`border-t border-border p-2 ${sidebarCollapsed ? 'flex flex-col items-center gap-1' : ''}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2 min-w-0 px-1 pb-2">
              <span className="w-7 h-7 rounded-full bg-ink-950 dark:bg-gold-500 text-gold-500 dark:text-ink-950 flex items-center justify-center text-[11px] font-semibold shrink-0">
                {(employee?.name || 'U').slice(0, 1).toUpperCase()}
              </span>
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[12px] font-medium text-foreground truncate">{employee?.name}</span>
                <span className="text-[10.5px] text-ink-500 truncate">{employee?.role}</span>
              </div>
            </div>
          )}
          <div className={`flex ${sidebarCollapsed ? 'flex-col gap-1' : 'items-center justify-between gap-1 pt-2 border-t border-border'}`}>
            <button
              onClick={toggleTheme}
              className="p-1.5 hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-500 hover:text-foreground rounded-md transition-colors"
              title={theme === 'light' ? 'Dark mode' : 'Light mode'}
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>
            <Link
              to="/change-password"
              className="p-1.5 hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-500 hover:text-foreground rounded-md transition-colors"
              title="Change password"
              aria-label="Change password"
            >
              <Lock className="w-4 h-4" />
            </Link>
            <button
              onClick={handleLogout}
              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/40 text-ink-500 hover:text-red-600 rounded-md transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main id="main-content" tabIndex={-1} className="flex-1 overflow-auto bg-background">
        <Outlet />
      </main>

      {showAssistantModal && (
        <UnifiedAssistantModal
          isOpen={showAssistantModal}
          onClose={() => setShowAssistantModal(false)}
        />
      )}
      <FloatingHelpBot />
    </div>
  );
};

export default Layout;
